/**
 * SecurityFormFields — per-engine + per-kind form rows for the
 * SecurityEditFormBody. Renders a focused, minimal set of options for each
 * (engine, kind, mode) combination — the most-common shapes from DSE2 §3.F1's
 * LoginDef / UserDef / RoleDef. The form does not attempt to cover every
 * opaque option (REQUIRE clauses, QUOTA text, VALID UNTIL, …) — power users
 * can drop into "Edit as SQL".
 */
import { useEffect } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { SecurityKind } from '../../inline/parseParkedSecurityStatement';
import {
  getAlterActions,
  isKindSupportedForEngine,
  normalizeAlterAction,
  type AlterActionId,
  type SecurityEditMode,
  type SecurityFormValues,
} from './securityFormHelpers';

export interface SecurityFormFieldsProps {
  readonly engine: SqlEngine;
  readonly mode: SecurityEditMode;
  readonly values: SecurityFormValues;
  readonly setValues: Dispatch<SetStateAction<SecurityFormValues>>;
  readonly testId: string;
}

const KIND_LABEL: Record<SecurityKind, string> = {
  login: 'Login',
  user: 'User',
  role: 'Role',
};

export function SecurityFormFields(props: SecurityFormFieldsProps) {
  const { engine, mode, values, setValues, testId } = props;

  const update = (patch: Partial<SecurityFormValues>) =>
    setValues((prev) => ({ ...prev, ...patch }));

  return (
    <div className="flex flex-col gap-3">
      <KindSelector
        engine={engine}
        kind={values.kind}
        onChange={(k) => update({ kind: k })}
        testId={testId}
      />
      <NameField
        kind={values.kind}
        value={values.name}
        onChange={(v) => update({ name: v })}
        testId={testId}
      />
      {engine === 'mysql' && (values.kind === 'user' || values.kind === 'role') ? (
        <Field label="Host" testId={`${testId}-host`}>
          <Input
            value={values.mysqlHost}
            placeholder="%"
            onChange={(e: ChangeEvent<HTMLInputElement>) => update({ mysqlHost: e.target.value })}
            data-testid={`${testId}-host-input`}
          />
        </Field>
      ) : null}

      {mode === 'create' ? (
        <CreateFields engine={engine} values={values} update={update} testId={testId} />
      ) : (
        <AlterFields engine={engine} values={values} update={update} testId={testId} />
      )}
    </div>
  );
}

function KindSelector({
  engine, kind, onChange, testId,
}: {
  engine: SqlEngine;
  kind: SecurityKind;
  onChange: (kind: SecurityKind) => void;
  testId: string;
}) {
  const all: SecurityKind[] = ['login', 'user', 'role'];
  return (
    <Field label="Kind" testId={`${testId}-kind`}>
      <div className="flex gap-1" data-testid={`${testId}-kind-group`}>
        {all.map((k) => {
          const enabled = isKindSupportedForEngine(engine, k);
          return (
            <Button
              key={k}
              type="button"
              variant={kind === k ? 'default' : 'outline'}
              size="sm"
              disabled={!enabled}
              onClick={() => onChange(k)}
              data-testid={`${testId}-kind-${k}`}
            >
              {KIND_LABEL[k]}
            </Button>
          );
        })}
      </div>
    </Field>
  );
}

function NameField({
  kind, value, onChange, testId,
}: {
  kind: SecurityKind;
  value: string;
  onChange: (v: string) => void;
  testId: string;
}) {
  return (
    <Field label={`${KIND_LABEL[kind]} name`} testId={`${testId}-name`}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-name-input`}
      />
    </Field>
  );
}

function CreateFields({
  engine, values, update, testId,
}: {
  engine: SqlEngine;
  values: SecurityFormValues;
  update: (patch: Partial<SecurityFormValues>) => void;
  testId: string;
}) {
  const { kind } = values;
  if (kind === 'login' && engine === 'mssql') {
    return (
      <>
        <Toggle
          label="Windows authentication"
          checked={values.mssqlFromWindows}
          onChange={(c) => update({ mssqlFromWindows: c })}
          testId={`${testId}-mssql-from-windows`}
        />
        {!values.mssqlFromWindows ? (
          <PasswordField values={values} update={update} testId={testId} />
        ) : null}
        <Field label="Default database" testId={`${testId}-mssql-default-db`}>
          <Input
            value={values.mssqlDefaultDatabase}
            onChange={(e) => update({ mssqlDefaultDatabase: e.target.value })}
            data-testid={`${testId}-mssql-default-db-input`}
          />
        </Field>
      </>
    );
  }
  if (kind === 'user' && engine === 'mssql') {
    return (
      <>
        <Field label="For login (existing)" testId={`${testId}-mssql-for-login`}>
          <Input
            value={values.mssqlForLogin}
            onChange={(e) => update({ mssqlForLogin: e.target.value })}
            disabled={values.mssqlWithoutLogin}
            data-testid={`${testId}-mssql-for-login-input`}
          />
        </Field>
        <Toggle
          label="Without login (contained user)"
          checked={values.mssqlWithoutLogin}
          onChange={(c) => update({ mssqlWithoutLogin: c, ...(c ? { mssqlForLogin: '' } : {}) })}
          testId={`${testId}-mssql-without-login`}
        />
        <Field label="Default schema" testId={`${testId}-mssql-default-schema`}>
          <Input
            value={values.mssqlDefaultSchema}
            onChange={(e) => update({ mssqlDefaultSchema: e.target.value })}
            data-testid={`${testId}-mssql-default-schema-input`}
          />
        </Field>
      </>
    );
  }
  if (kind === 'role' && engine === 'mssql') {
    return (
      <Field label="Authorization (owner)" testId={`${testId}-mssql-auth`}>
        <Input
          value={values.mssqlAuthorization}
          onChange={(e) => update({ mssqlAuthorization: e.target.value })}
          data-testid={`${testId}-mssql-auth-input`}
        />
      </Field>
    );
  }
  if (kind === 'role' && engine === 'postgres') {
    return (
      <>
        <Toggle
          label="LOGIN attribute (login-capable role)"
          checked={values.pgsqlLogin}
          onChange={(c) => update({ pgsqlLogin: c })}
          testId={`${testId}-pgsql-login`}
        />
        <Toggle
          label="SUPERUSER"
          checked={values.pgsqlSuperuser}
          onChange={(c) => update({ pgsqlSuperuser: c })}
          testId={`${testId}-pgsql-superuser`}
        />
        {values.pgsqlLogin ? (
          <PasswordField values={values} update={update} testId={testId} />
        ) : null}
      </>
    );
  }
  if (kind === 'user' && engine === 'mysql') {
    return <PasswordField values={values} update={update} testId={testId} />;
  }
  if (kind === 'user' && engine === 'oracle') {
    return (
      <>
        <Toggle
          label="Identified externally (OS auth)"
          checked={values.oracleIdentifiedExternally}
          onChange={(c) => update({ oracleIdentifiedExternally: c })}
          testId={`${testId}-oracle-external`}
        />
        {!values.oracleIdentifiedExternally ? (
          <PasswordField values={values} update={update} testId={testId} />
        ) : null}
      </>
    );
  }
  if (kind === 'role' && engine === 'oracle') {
    return <PasswordField values={values} update={update} testId={testId} optional />;
  }
  // role on mysql — name + host are enough; no extra fields.
  return null;
}

function AlterFields({
  engine, values, update, testId,
}: {
  engine: SqlEngine;
  values: SecurityFormValues;
  update: (patch: Partial<SecurityFormValues>) => void;
  testId: string;
}) {
  const { kind } = values;
  const actions = getAlterActions(engine, kind);

  // F38: `alterAction` seeds from the global default ('rotate-password') and is
  // never normalized to a valid action for the active (engine, kind) — so for a
  // combo that doesn't offer it (e.g. mssql/role) the Action control shows a
  // value the form isn't actually holding: the Radix trigger renders EMPTY,
  // because <SelectValue> has no <SelectItem> matching 'rotate-password' (the
  // native <select> it replaced showed the first option, 'Add member', instead —
  // a different symptom of the same mismatch). Either way every detail
  // conditional is false and no detail input appears. Reset to the first valid
  // action when the current one is illegal (a still-valid deliberate selection
  // is preserved), which also makes the trigger label match the held value.
  useEffect(() => {
    const next = normalizeAlterAction(actions, values.alterAction);
    if (next !== undefined && next !== values.alterAction) {
      update({ alterAction: next });
    }
    // `actions` is recomputed each render; key on its identity-stable contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, kind, values.alterAction]);

  return (
    <>
      <Field label="Action" testId={`${testId}-alter-action`}>
        <Select
          value={values.alterAction}
          onValueChange={(v) => update({ alterAction: v as AlterActionId })}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`${testId}-alter-action-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {actions.map((a) => (
              <SelectItem key={a} value={a}>{ACTION_LABEL[a]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {(values.alterAction === 'rotate-password') ? (
        <PasswordField values={values} update={update} testId={testId} />
      ) : null}
      {(values.alterAction === 'rename') ? (
        <Field label="New name" testId={`${testId}-rename-to`}>
          <Input
            value={values.alterRenameTo}
            onChange={(e) => update({ alterRenameTo: e.target.value })}
            data-testid={`${testId}-rename-to-input`}
          />
        </Field>
      ) : null}
      {(values.alterAction === 'add-member') ? (
        <Field label="Member to add" testId={`${testId}-add-member`}>
          <Input
            value={values.alterAddMember}
            onChange={(e) => update({ alterAddMember: e.target.value })}
            data-testid={`${testId}-add-member-input`}
          />
        </Field>
      ) : null}
      {(values.alterAction === 'drop-member') ? (
        <Field label="Member to drop" testId={`${testId}-drop-member`}>
          <Input
            value={values.alterDropMember}
            onChange={(e) => update({ alterDropMember: e.target.value })}
            data-testid={`${testId}-drop-member-input`}
          />
        </Field>
      ) : null}
    </>
  );
}

const ACTION_LABEL: Record<AlterActionId, string> = {
  'rotate-password': 'Rotate password',
  rename: 'Rename',
  'add-member': 'Add member',
  'drop-member': 'Drop member',
  lock: 'Lock account',
  unlock: 'Unlock account',
  enable: 'Enable',
  disable: 'Disable',
};

function PasswordField({
  values, update, testId, optional,
}: {
  values: SecurityFormValues;
  update: (patch: Partial<SecurityFormValues>) => void;
  testId: string;
  optional?: boolean;
}) {
  return (
    <Field label={optional ? 'Password (optional)' : 'Password'} testId={`${testId}-password`}>
      <Input
        type="password"
        value={values.password}
        onChange={(e) => update({ password: e.target.value })}
        data-testid={`${testId}-password-input`}
      />
    </Field>
  );
}

function Field({
  label, children, testId,
}: { label: string; children: React.ReactNode; testId: string }) {
  return (
    <div className="flex flex-col gap-1" data-testid={`${testId}-field`}>
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({
  label, checked, onChange, testId,
}: { label: string; checked: boolean; onChange: (c: boolean) => void; testId: string }) {
  return (
    <label className="flex items-center gap-2 text-xs" data-testid={testId}>
      <Checkbox
        checked={checked}
        onCheckedChange={(c) => onChange(c === true)}
        data-testid={`${testId}-input`}
      />
      <span>{label}</span>
    </label>
  );
}
